// src/pages/DatosContactoProveedores.js
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    FaAddressBook,
    FaCheck,
    FaEdit,
    FaEnvelope,
    FaFileAlt,
    FaFileUpload,
    FaIdCard,
    FaMapMarkerAlt,
    FaPhone,
    FaPlus,
    FaSearch,
    FaStickyNote,
    FaTimes,
    FaTrash,
    FaUniversity,
    FaUser,
} from 'react-icons/fa';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import './DatosContactoProveedores.css';
import './DatosContactoProveedoresMobile.css';
import { registerMovement } from '../utils/registerMovement';
import ConfirmDialog from '../components/ConfirmDialog';
import * as pdfjsLib from 'pdfjs-dist';

// Configurar el worker de PDF.js desde la carpeta public (evita problemas de CORS)
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const EMPTY_PROVIDER = {
    nombreEmpresa: '',
    nombreContacto: '',
    telefono: '',
    email: '',
    direccion: '',
    rfc: '',
    razonSocial: '',
    regimenFiscal: '',
    usoCfdi: '',
    banco: '',
    clabe: '',
    metodoPago: '',
    formaPago: '',
    notas: '',
    documentoUrl: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const EMPTY_FACTURACION = {
    nombreFraccionamiento: '',
    razonSocial: '',
    rfc: '',
    regimenFiscal: '',
    usoCfdi: '',
    domicilioFiscal: '',
    telefono: '',
    emailFacturacion: '',
    notas: '',
};

const filterProviders = (providers, term) => {
    if (!term) {
        return providers;
    }
    const normalized = term.trim().toLowerCase();
    return providers.filter((provider) => {
        const haystack = [
            provider.nombreEmpresa,
            provider.nombreContacto,
            provider.telefono,
            provider.email,
            provider.rfc,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalized);
    });
};

// Función para extraer datos del PDF
const extractDataFromPDF = async (file) => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';

        // Extraer texto de todas las páginas
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += ' ' + pageText;
        }

        // Extraer datos usando expresiones regulares
        const extractedData = {};

        // RFC - buscar patrón "RFC: XXXXXX..."
        const rfcMatch = fullText.match(/RFC[:\s]+([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3})/i);
        if (rfcMatch) {
            extractedData.rfc = rfcMatch[1].toUpperCase();
        }

        // Nombre Comercial (para Nombre de Contacto)
        const nombreComercialMatch = fullText.match(/Nombre\s+Comercial[:\s]+([A-ZÁÉÍÓÚÑ\s]{3,50})(?=\s+Nombre\s+del|$)/i);
        if (nombreComercialMatch) {
            const comercial = nombreComercialMatch[1].trim();
            extractedData.nombreContacto = comercial.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        }

        // Nombre (s), Primer Apellido, Segundo Apellido (para Nombre de Contacto - alternativa)
        if (!extractedData.nombreContacto) {
            // Capturar TODOS los nombres (pueden ser 1, 2 o más palabras) hasta "Primer"
            const nombreMatch = fullText.match(/Nombre\s*\([s]?\)\s*[:\s]+([A-ZÁÉÍÓÚÑ\s]{2,60})(?=\s+Primer\s+Apellido)/i);
            const primerApellidoMatch = fullText.match(/Primer\s+Apellido\s*[:\s]+([A-ZÁÉÍÓÚÑ]{2,30})(?=\s+Segundo\s+Apellido)/i);
            const segundoApellidoMatch = fullText.match(/Segundo\s+Apellido\s*[:\s]+([A-ZÁÉÍÓÚÑ]{2,30})/i);

            let nombreCompleto = [];
            if (nombreMatch) {
                // Dividir nombres si hay múltiples (ej: "GIOVANNI MICHAEL" o "ALFREDO")
                const nombres = nombreMatch[1].trim().split(/\s+/);
                nombres.forEach(nombre => {
                    if (nombre.length > 1) {
                        nombreCompleto.push(nombre.charAt(0).toUpperCase() + nombre.slice(1).toLowerCase());
                    }
                });
            }
            if (primerApellidoMatch) {
                const apellido = primerApellidoMatch[1].trim();
                nombreCompleto.push(apellido.charAt(0).toUpperCase() + apellido.slice(1).toLowerCase());
            }
            if (segundoApellidoMatch) {
                const apellido = segundoApellidoMatch[1].trim();
                nombreCompleto.push(apellido.charAt(0).toUpperCase() + apellido.slice(1).toLowerCase());
            }

            if (nombreCompleto.length > 0) {
                extractedData.nombreContacto = nombreCompleto.join(' ');
            }
        }

        // Dirección - Formato: Tipo Vialidad + Vialidad + Número Exterior, Colonia, Municipio, Estado
        const direccionPartes = [];

        // Extraer Tipo de Vialidad (CALLE, AVENIDA, CAMINO, etc.)
        const tipoVialidadMatch = fullText.match(/Tipo\s+de\s+Vialidad\s*[:\s]+([A-ZÁÉÍÓÚÑ\s]{2,20})(?=\s+Nombre\s+de\s+Vialidad)/i);
        let tipoVialidad = '';
        if (tipoVialidadMatch) {
            tipoVialidad = tipoVialidadMatch[1].trim();
        }

        // Extraer Nombre de Vialidad
        const vialidadMatch = fullText.match(/Nombre\s+de\s+Vialidad\s*[:\s]+([A-ZÁÉÍÓÚÑ0-9\s.]{2,60})(?=\s+Número\s+Exterior)/i);
        let vialidad = '';
        if (vialidadMatch) {
            vialidad = vialidadMatch[1].trim();
        }

        // Extraer Número Exterior (puede ser "SIN NUMERO" o un número)
        const numeroExteriorMatch = fullText.match(/Número\s+Exterior\s*[:\s]+([A-ZÁÉÍÓÚÑ0-9\s]{1,20})(?=\s+Número\s+Interior)/i);
        let numeroExterior = '';
        if (numeroExteriorMatch) {
            const numero = numeroExteriorMatch[1].trim();
            if (numero.toUpperCase() !== 'SIN NUMERO') {
                numeroExterior = numero;
            }
        }

        // Combinar: "Tipo Vialidad + Vialidad + Número"
        if (tipoVialidad || vialidad || numeroExterior) {
            const parteVialidad = [tipoVialidad, vialidad, numeroExterior]
                .filter(p => p.length > 0)
                .join(' ');
            if (parteVialidad.trim()) {
                direccionPartes.push(parteVialidad.trim());
            }
        }

        // Extraer Colonia
        const coloniaMatch = fullText.match(/Nombre\s+de\s+la\s+Colonia\s*[:\s]+([A-ZÁÉÍÓÚÑ\s.]{2,50})(?=\s+Nombre\s+de\s+la\s+Localidad)/i);
        if (coloniaMatch) {
            const colonia = coloniaMatch[1].trim();
            const coloniaFormatted = colonia.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
            direccionPartes.push(coloniaFormatted);
        }

        // Extraer Municipio
        const municipioMatch = fullText.match(/Nombre\s+del\s+Municipio\s+o\s+Demarcación\s+Territorial\s*[:\s]+([A-ZÁÉÍÓÚÑ\s.]{2,50})(?=\s+Nombre\s+de\s+la\s+Entidad)/i);
        if (municipioMatch) {
            const municipio = municipioMatch[1].trim();
            const municipioFormatted = municipio.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
            direccionPartes.push(municipioFormatted);
        }

        // Extraer Estado
        const estadoMatch = fullText.match(/Nombre\s+de\s+la\s+Entidad\s+Federativa\s*[:\s]+([A-ZÁÉÍÓÚÑ\s]{2,30})/i);
        if (estadoMatch) {
            const estado = estadoMatch[1].trim();
            direccionPartes.push(estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase());
        }

        if (direccionPartes.length > 0) {
            extractedData.direccion = direccionPartes.join(', ');
        }

        return extractedData;
    } catch (error) {
        console.error('Error extrayendo datos del PDF:', error);
        throw new Error('No se pudo extraer información del PDF. Verifica que sea un PDF válido.');
    }
};

const DatosContactoProveedores = () => {
    const { userData } = useAuth();
    const fraccionamientoId = useMemo(
        () => userData?.fraccionamientoId || userData?.fraccionamientoTd || null,
        [userData],
    );
    const normalizedRole = useMemo(() => String(userData?.rol || '').toLowerCase(), [userData]);
    const canEdit = normalizedRole === 'superadmin';

    const actorName = useMemo(() => {
        const candidates = [
            userData?.nombreCompleto,
            userData?.nombre,
            userData?.displayName,
            userData?.email,
        ];
        return candidates.find((value) => typeof value === 'string' && value.trim().length > 0) || 'Usuario';
    }, [userData]);

    const actorEmail = useMemo(() => {
        if (typeof userData?.email === 'string' && userData.email.trim().length > 0) {
            return userData.email.trim().toLowerCase();
        }
        return null;
    }, [userData]);

    const [activeTab, setActiveTab] = useState('agenda');

    const [providers, setProviders] = useState([]);
    const [providersLoading, setProvidersLoading] = useState(true);
    const [selectedProviderId, setSelectedProviderId] = useState(null);
    const [providerForm, setProviderForm] = useState(EMPTY_PROVIDER);
    const [filterTerm, setFilterTerm] = useState('');
    const [savingProvider, setSavingProvider] = useState(false);
    const [providerMessage, setProviderMessage] = useState('');
    const [isProviderFormVisible, setProviderFormVisible] = useState(false);
    const [providerToDelete, setProviderToDelete] = useState(null);
    const [deletingProvider, setDeletingProvider] = useState(false);

    const [facturacion, setFacturacion] = useState(EMPTY_FACTURACION);
    const [facturacionBase, setFacturacionBase] = useState(EMPTY_FACTURACION);
    const [facturacionLoaded, setFacturacionLoaded] = useState(false);
    const [savingFacturacion, setSavingFacturacion] = useState(false);
    const [facturacionMessage, setFacturacionMessage] = useState('');
    const [isEditingFacturacion, setIsEditingFacturacion] = useState(false);

    const isEditingFacturacionRef = useRef(false);
    useEffect(() => {
        isEditingFacturacionRef.current = isEditingFacturacion;
    }, [isEditingFacturacion]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setProviders([]);
            setProvidersLoading(false);
            setProviderFormVisible(false);
            return () => { };
        }

        const proveedoresRef = collection(db, 'fraccionamientos', fraccionamientoId, 'proveedores');
        const proveedoresQuery = query(proveedoresRef, orderBy('nombreEmpresa'));

        const unsubscribe = onSnapshot(
            proveedoresQuery,
            (snapshot) => {
                const items = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
                setProviders(items);
                setProvidersLoading(false);
                if (!canEdit && items.length > 0) {
                    setSelectedProviderId((prevId) => prevId || items[0].id);
                }
            },
            (error) => {
                console.error('DatosContactoProveedores: error al leer proveedores', error);
                setProviders([]);
                setProvidersLoading(false);
                setProviderFormVisible(false);
            },
        );

        return () => unsubscribe();
    }, [fraccionamientoId, canEdit]);

    useEffect(() => {
        if (selectedProviderId) {
            const selected = providers.find((item) => item.id === selectedProviderId);
            if (selected) {
                setProviderForm({ ...EMPTY_PROVIDER, ...selected });
                return;
            }
        }
        setProviderForm({ ...EMPTY_PROVIDER });
    }, [providers, selectedProviderId]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setFacturacion(EMPTY_FACTURACION);
            setFacturacionBase(EMPTY_FACTURACION);
            setFacturacionLoaded(true);
            setIsEditingFacturacion(false);
            return () => { };
        }

        const facturacionRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'datos_facturacion');
        const unsubscribe = onSnapshot(
            facturacionRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data();
                    const merged = { ...EMPTY_FACTURACION, ...data };
                    setFacturacionBase(merged);
                    if (!isEditingFacturacionRef.current) {
                        setFacturacion(merged);
                    }
                } else {
                    setFacturacionBase(EMPTY_FACTURACION);
                    if (!isEditingFacturacionRef.current) {
                        setFacturacion(EMPTY_FACTURACION);
                    }
                }
                setFacturacionLoaded(true);
            },
            (error) => {
                console.error('DatosContactoProveedores: error al leer datos de facturación', error);
                setFacturacion(EMPTY_FACTURACION);
                setFacturacionBase(EMPTY_FACTURACION);
                setFacturacionLoaded(true);
            },
        );

        return () => unsubscribe();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!canEdit && isEditingFacturacion) {
            setIsEditingFacturacion(false);
        }
    }, [canEdit, isEditingFacturacion]);

    useEffect(() => {
        if (providerMessage) {
            const timer = setTimeout(() => setProviderMessage(''), 3500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [providerMessage]);

    useEffect(() => {
        if (facturacionMessage) {
            const timer = setTimeout(() => setFacturacionMessage(''), 3500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [facturacionMessage]);

    const handleSelectProvider = (provider) => {
        setSelectedProviderId(provider?.id || null);
        setProviderForm({ ...EMPTY_PROVIDER, ...provider });
        if (canEdit) {
            setProviderFormVisible(!!provider);
        }
    };

    const handleNewProvider = () => {
        setSelectedProviderId(null);
        setProviderForm({ ...EMPTY_PROVIDER });
        setProviderFormVisible(true);
    };

    const handleProviderChange = (field, value) => {
        setProviderForm((prev) => ({ ...prev, [field]: value }));
    };

    const handlePDFUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.includes('pdf')) {
            setProviderMessage('Por favor carga un archivo PDF válido.');
            return;
        }

        try {
            setProviderMessage('Extrayendo información del PDF...');
            const extractedData = await extractDataFromPDF(file);

            // Actualizar los campos con los datos extraídos
            setProviderForm((prev) => {
                const updated = { ...prev };

                // RFC
                if (extractedData.rfc) {
                    updated.rfc = extractedData.rfc;
                }

                // Nombre de Contacto
                if (extractedData.nombreContacto) {
                    updated.nombreContacto = extractedData.nombreContacto;
                }

                // Dirección
                if (extractedData.direccion) {
                    updated.direccion = extractedData.direccion;
                }

                // Razón social = Nombre del proveedor
                if (updated.nombreEmpresa) {
                    updated.razonSocial = updated.nombreEmpresa;
                }

                return updated;
            });

            setProviderMessage('Información extraída correctamente. Revisa los campos y completa los que falten.');

            // Limpiar el input del archivo
            event.target.value = '';
        } catch (error) {
            setProviderMessage(`Error: ${error.message}`);
            event.target.value = '';
        }
    };

    // Manejador para PDF de datos de facturación del fraccionamiento
    const handlePDFUploadFacturacion = async (event) => {
        const file = event.target.files[0];
        if (!file || file.type !== 'application/pdf') {
            setFacturacionMessage('Por favor selecciona un archivo PDF válido.');
            return;
        }

        setFacturacionMessage('Extrayendo información del PDF...');

        try {
            const extractedData = await extractDataFromPDF(file);

            // Actualizar los campos de facturación con los datos extraídos
            setFacturacion(prev => {
                const updated = { ...prev };

                if (extractedData.rfc) {
                    updated.rfc = extractedData.rfc;
                }
                if (extractedData.nombreContacto) {
                    // Para el fraccionamiento, usar como razón social
                    updated.razonSocial = extractedData.nombreContacto;
                }
                if (extractedData.direccion) {
                    updated.domicilioFiscal = extractedData.direccion;
                }

                return updated;
            });

            setFacturacionMessage('Información extraída correctamente. Revisa los campos y completa los que falten.');

            // Limpiar el input del archivo
            event.target.value = '';
        } catch (error) {
            setFacturacionMessage(`Error: ${error.message}`);
            event.target.value = '';
        }
    };

    const logProviderMovement = useCallback(async ({ action, providerId, payload }) => {
        if (!fraccionamientoId) {
            return;
        }
        try {
            const actionLabels = {
                create: 'agregó un proveedor',
                update: 'actualizó un proveedor',
                delete: 'eliminó un proveedor',
            };
            const tituloLabels = {
                create: 'Proveedor registrado',
                update: 'Proveedor actualizado',
                delete: 'Proveedor eliminado',
            };
            const titulo = tituloLabels[action] || 'Movimiento en proveedores';
            const descripcionBase = actionLabels[action] || 'realizó cambios en proveedores';
            const nombreProveedor = payload?.nombreEmpresa || payload?.razonSocial || 'Proveedor sin nombre';

            await registerMovement({
                fraccionamientoId,
                titulo,
                descripcion: `${actorName} ${descripcionBase}: ${nombreProveedor}.`,
                categoria: 'Proveedores',
                tipoMovimiento: `proveedor_${action}`,
                origen: 'contacto_proveedores',
                metadata: {
                    providerId,
                    nombreEmpresa: payload?.nombreEmpresa || null,
                    nombreContacto: payload?.nombreContacto || null,
                    telefono: payload?.telefono || null,
                    email: payload?.email || null,
                    rfc: payload?.rfc || null,
                    razonSocial: payload?.razonSocial || null,
                    banco: payload?.banco || null,
                    actorNombre: actorName,
                    actorEmail,
                    accion: action,
                },
            });
        } catch (movementError) {
            console.error('DatosContactoProveedores: no se pudo registrar el movimiento en notificaciones', movementError);
        }
    }, [actorEmail, actorName, fraccionamientoId]);

    const logFacturacionMovement = useCallback(async (payload) => {
        if (!fraccionamientoId) {
            return;
        }
        try {
            await registerMovement({
                fraccionamientoId,
                titulo: 'Datos de facturación actualizados',
                descripcion: `${actorName} actualizó la información de facturación del fraccionamiento.`,
                categoria: 'Configuraciones',
                tipoMovimiento: 'facturacion_actualizada',
                origen: 'contacto_proveedores',
                metadata: {
                    ...payload,
                    actorNombre: actorName,
                    actorEmail,
                },
            });
        } catch (movementError) {
            console.error('DatosContactoProveedores: no se pudo registrar el movimiento de facturación', movementError);
        }
    }, [actorEmail, actorName, fraccionamientoId]);

    const handleSubmitProvider = async (event) => {
        event.preventDefault();
        if (!canEdit || !fraccionamientoId) {
            return;
        }
        const trimmedName = providerForm.nombreEmpresa.trim();
        if (!trimmedName) {
            setProviderMessage('Ingresa el nombre del proveedor.');
            return;
        }
        setSavingProvider(true);
        try {
            const collectionRef = collection(db, 'fraccionamientos', fraccionamientoId, 'proveedores');
            const payload = {
                ...providerForm,
                nombreEmpresa: trimmedName,
                nombreContacto: providerForm.nombreContacto.trim(),
                telefono: providerForm.telefono.trim(),
                email: providerForm.email.trim().toLowerCase(),
                direccion: providerForm.direccion.trim(),
                rfc: providerForm.rfc.trim().toUpperCase(),
                razonSocial: providerForm.razonSocial.trim(),
                regimenFiscal: providerForm.regimenFiscal.trim(),
                usoCfdi: providerForm.usoCfdi.trim(),
                banco: providerForm.banco.trim(),
                clabe: providerForm.clabe.trim(),
                metodoPago: providerForm.metodoPago.trim(),
                formaPago: providerForm.formaPago.trim(),
                notas: providerForm.notas.trim(),
                documentoUrl: providerForm.documentoUrl.trim(),
                actualizadoEn: serverTimestamp(),
            };

            if (selectedProviderId) {
                const providerRef = doc(collectionRef, selectedProviderId);
                await updateDoc(providerRef, payload);
                setProviderMessage('Proveedor actualizado correctamente.');
                await logProviderMovement({ action: 'update', providerId: selectedProviderId, payload });
            } else {
                const newDoc = await addDoc(collectionRef, {
                    ...payload,
                    creadoEn: serverTimestamp(),
                });
                setSelectedProviderId(newDoc.id);
                setProviderMessage('Proveedor agregado correctamente.');
                await logProviderMovement({ action: 'create', providerId: newDoc.id, payload });
            }
        } catch (error) {
            console.error('DatosContactoProveedores: error al guardar proveedor', error);
            setProviderMessage('No se pudo guardar el proveedor. Intenta nuevamente.');
        } finally {
            setSavingProvider(false);
        }
    };

    const promptDeleteProvider = (provider) => {
        if (!canEdit || !provider?.id) {
            return;
        }
        setProviderMessage('');
        setProviderToDelete(provider);
    };

    const handleConfirmDeleteProvider = async () => {
        if (!canEdit || !fraccionamientoId || !providerToDelete?.id) {
            setProviderToDelete(null);
            return;
        }
        const providerId = providerToDelete.id;
        const selected = providers.find((item) => item.id === providerId) || providerToDelete;
        setDeletingProvider(true);
        try {
            const providerRef = doc(db, 'fraccionamientos', fraccionamientoId, 'proveedores', providerId);
            await deleteDoc(providerRef);
            setProviderMessage('Proveedor eliminado.');
            if (selectedProviderId === providerId) {
                setSelectedProviderId(null);
                setProviderForm({ ...EMPTY_PROVIDER });
                setProviderFormVisible(false);
            }
            await logProviderMovement({ action: 'delete', providerId, payload: selected || {} });
        } catch (error) {
            console.error('DatosContactoProveedores: error al eliminar proveedor', error);
            setProviderMessage('No se pudo eliminar el proveedor.');
        } finally {
            setDeletingProvider(false);
            setProviderToDelete(null);
        }
    };

    const handleFacturacionChange = (field, value) => {
        setFacturacion((prev) => ({ ...prev, [field]: value }));
    };

    const handleStartFacturacionEdit = () => {
        if (!canEdit) {
            return;
        }
        setFacturacion(facturacionBase);
        setFacturacionMessage('');
        setIsEditingFacturacion(true);
    };

    const handleCancelFacturacionEdit = () => {
        setFacturacion(facturacionBase);
        setSavingFacturacion(false);
        setIsEditingFacturacion(false);
        setFacturacionMessage('');
    };

    const handleSaveFacturacion = async (event) => {
        event.preventDefault();
        if (!canEdit || !fraccionamientoId || !isEditingFacturacion) {
            return;
        }

        const rawTelefono = facturacion.telefono.trim();
        const phoneDigits = rawTelefono.replace(/\D/g, '');
        if (rawTelefono !== '' && phoneDigits.length !== 10) {
            setFacturacionMessage('El teléfono debe contener exactamente 10 dígitos.');
            return;
        }

        const rawEmail = facturacion.emailFacturacion.trim().toLowerCase();
        if (rawEmail !== '' && !EMAIL_REGEX.test(rawEmail)) {
            setFacturacionMessage('Ingresa un correo electrónico válido (ejemplo@dominio.com).');
            return;
        }

        setSavingFacturacion(true);
        try {
            const facturacionRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'datos_facturacion');
            const sanitized = {
                nombreFraccionamiento: facturacion.nombreFraccionamiento.trim(),
                razonSocial: facturacion.razonSocial.trim(),
                rfc: facturacion.rfc.trim().toUpperCase(),
                regimenFiscal: facturacion.regimenFiscal.trim(),
                usoCfdi: facturacion.usoCfdi.trim(),
                domicilioFiscal: facturacion.domicilioFiscal.trim(),
                telefono: rawTelefono,
                emailFacturacion: rawEmail,
                notas: facturacion.notas.trim(),
            };
            const updatedAtIso = new Date().toISOString();
            await setDoc(
                facturacionRef,
                {
                    ...sanitized,
                    actualizadoEn: serverTimestamp(),
                },
                { merge: true },
            );
            setFacturacionMessage('Datos de facturación guardados correctamente.');
            await logFacturacionMovement({ ...sanitized, actualizadoEnIso: updatedAtIso });
            setIsEditingFacturacion(false);
        } catch (error) {
            console.error('DatosContactoProveedores: error al guardar datos de facturación', error);
            setFacturacionMessage('No se pudieron guardar los datos.');
        } finally {
            setSavingFacturacion(false);
        }
    };

    const filteredProviders = useMemo(
        () => filterProviders(providers, filterTerm),
        [providers, filterTerm],
    );

    return (
        <div className="contacto-proveedores-page">
            <header className="cp-header">
                <div className="cp-header-content">
                    <div className="cp-header-icon">
                        <FaAddressBook />
                    </div>
                    <div className="cp-header-text">
                        <h1>Datos de Contacto y Proveedores</h1>
                        <p>Centraliza a tus aliados estratégicos y mantén actualizada la información fiscal del fraccionamiento.</p>
                    </div>
                </div>
            </header>

            <div className="cp-tabs" role="tablist">
                <button
                    type="button"
                    className={`cp-tab-button ${activeTab === 'agenda' ? 'active' : ''}`}
                    onClick={() => setActiveTab('agenda')}
                    role="tab"
                    aria-selected={activeTab === 'agenda'}
                >
                    Agenda de Proveedores
                </button>
                <button
                    type="button"
                    className={`cp-tab-button ${activeTab === 'facturacion' ? 'active' : ''}`}
                    onClick={() => setActiveTab('facturacion')}
                    role="tab"
                    aria-selected={activeTab === 'facturacion'}
                >
                    Datos de Facturación
                </button>
            </div>

            {activeTab === 'agenda' && (
                <div className="cp-agenda" role="tabpanel" aria-label="Agenda de proveedores">
                    <div className="cp-list-panel">
                        <div className="cp-list-toolbar">
                            <div className="cp-search-box" style={{ position: 'relative' }}>
                                <input
                                    type="search"
                                    placeholder="Buscar por nombre, contacto o RFC..."
                                    value={filterTerm}
                                    onChange={(event) => setFilterTerm(event.target.value)}
                                    aria-label="Buscar proveedores"
                                />
                            </div>
                            {canEdit && (
                                <button type="button" className="cp-primary" onClick={handleNewProvider}>
                                    <FaPlus />
                                    <span>Nuevo Proveedor</span>
                                </button>
                            )}
                        </div>
                        <div className="cp-provider-list">
                            {providersLoading ? (
                                <p className="cp-muted">Cargando proveedores...</p>
                            ) : filteredProviders.length === 0 ? (
                                <p className="cp-muted">Aún no hay proveedores registrados.</p>
                            ) : (
                                filteredProviders.map((provider) => (
                                    <article
                                        key={provider.id}
                                        className={`cp-provider-card ${selectedProviderId === provider.id ? 'active' : ''}`}
                                        onClick={() => handleSelectProvider(provider)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                handleSelectProvider(provider);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <h3>{provider.nombreEmpresa || 'Proveedor sin nombre'}</h3>
                                        <ul>
                                            {provider.nombreContacto && <li><span>Contacto:</span> {provider.nombreContacto}</li>}
                                            {provider.telefono && <li><span>Teléfono:</span> {provider.telefono}</li>}
                                            {provider.email && <li><span>Email:</span> {provider.email}</li>}
                                            {provider.rfc && <li><span>RFC:</span> {provider.rfc}</li>}
                                        </ul>
                                        {canEdit && (
                                            <div className="cp-card-actions">
                                                <button
                                                    type="button"
                                                    className="cp-link"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleSelectProvider(provider);
                                                    }}
                                                    title="Editar"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cp-link cp-danger-link"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        promptDeleteProvider(provider);
                                                    }}
                                                    title="Eliminar"
                                                    style={{ color: '#ef4444' }}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        )}
                                    </article>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="cp-form-panel">
                        {providerMessage && <div className="cp-feedback-message">{providerMessage}</div>}
                        {canEdit ? (
                            isProviderFormVisible ? (
                                <form className="cp-form" onSubmit={handleSubmitProvider}>
                                    <div className="cp-form-header">
                                        <h2>{selectedProviderId ? 'Editar Proveedor' : 'Registrar Nuevo Proveedor'}</h2>
                                        <button type="button" className="cp-close-mini" onClick={() => setProviderFormVisible(false)} title="Cerrar">
                                            <FaTimes />
                                        </button>
                                    </div>

                                    {/* Sección de carga de PDF */}
                                    <div className="cp-section">
                                        <div className="cp-section-header">
                                            <FaFileUpload />
                                            <h3>Cargar Documento (Opcional)</h3>
                                        </div>
                                        <div className="cp-pdf-upload-section">
                                            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem', textAlign: 'center' }}>
                                                Carga un PDF con los datos fiscales para extraer automáticamente la información principal.
                                            </p>
                                            <label htmlFor="pdf-upload" className="cp-pdf-upload-label" style={{ display: 'block' }}>
                                                <input
                                                    id="pdf-upload"
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={handlePDFUpload}
                                                    style={{ display: 'none' }}
                                                />
                                                <div className="cp-pdf-upload-button">
                                                    <FaFileUpload size={32} />
                                                    <span>Seleccionar PDF</span>
                                                    <small style={{ opacity: 0.7 }}>Extraer RFC, Dirección y Contacto</small>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="cp-section">
                                        <div className="cp-section-header">
                                            <FaUser />
                                            <h3>Datos Generales</h3>
                                        </div>
                                        <div className="cp-fields-grid">
                                            <div className="cp-field cp-field--full">
                                                <label htmlFor="prov-nombre">Nombre del proveedor *</label>
                                                <input
                                                    id="prov-nombre"
                                                    type="text"
                                                    value={providerForm.nombreEmpresa}
                                                    onChange={(event) => handleProviderChange('nombreEmpresa', event.target.value)}
                                                    placeholder="Ej. Construcciones García S.A."
                                                    required
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-contacto">Nombre de contacto</label>
                                                <input
                                                    id="prov-contacto"
                                                    type="text"
                                                    value={providerForm.nombreContacto}
                                                    onChange={(event) => handleProviderChange('nombreContacto', event.target.value)}
                                                    placeholder="Persona de contacto"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-telefono">Teléfono</label>
                                                <input
                                                    id="prov-telefono"
                                                    type="tel"
                                                    value={providerForm.telefono}
                                                    onChange={(event) => handleProviderChange('telefono', event.target.value)}
                                                    placeholder="442-123-4567"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-email">Correo electrónico</label>
                                                <input
                                                    id="prov-email"
                                                    type="email"
                                                    value={providerForm.email}
                                                    onChange={(event) => handleProviderChange('email', event.target.value)}
                                                    placeholder="contacto@empresa.com"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-direccion">Dirección</label>
                                                <input
                                                    id="prov-direccion"
                                                    type="text"
                                                    value={providerForm.direccion}
                                                    onChange={(event) => handleProviderChange('direccion', event.target.value)}
                                                    placeholder="Calle, colonia, ciudad"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cp-section">
                                        <div className="cp-section-header">
                                            <FaIdCard />
                                            <h3>Datos Fiscales</h3>
                                        </div>
                                        <div className="cp-fields-grid">
                                            <div className="cp-field">
                                                <label htmlFor="prov-rfc">RFC</label>
                                                <input
                                                    id="prov-rfc"
                                                    type="text"
                                                    value={providerForm.rfc}
                                                    onChange={(event) => handleProviderChange('rfc', event.target.value.toUpperCase())}
                                                    placeholder="CGA920515XX1"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-razon">Razón social</label>
                                                <input
                                                    id="prov-razon"
                                                    type="text"
                                                    value={providerForm.razonSocial}
                                                    onChange={(event) => handleProviderChange('razonSocial', event.target.value)}
                                                    placeholder="Nombre fiscal"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-regimen">Régimen fiscal</label>
                                                <select
                                                    id="prov-regimen"
                                                    value={providerForm.regimenFiscal}
                                                    onChange={(event) => handleProviderChange('regimenFiscal', event.target.value)}
                                                    disabled={savingProvider}
                                                >
                                                    <option value="">Selecciona un régimen fiscal</option>
                                                    <option value="601 - General de Ley Personas Morales">601 - General de Ley Personas Morales</option>
                                                    <option value="603 - Personas Morales con Fines no Lucrativos">603 - Personas Morales con Fines no Lucrativos</option>
                                                    <option value="605 - Sueldos y Salarios e Ingresos Asimilados a Salarios">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                                                    <option value="606 - Arrendamiento">606 - Arrendamiento</option>
                                                    <option value="607 - Régimen de Enajenación o Adquisición de Bienes">607 - Régimen de Enajenación o Adquisición de Bienes</option>
                                                    <option value="608 - Demás ingresos">608 - Demás ingresos</option>
                                                    <option value="610 - Residentes en el Extranjero sin Establecimiento Permanente en México">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</option>
                                                    <option value="611 - Ingresos por Dividendos (socios y accionistas)">611 - Ingresos por Dividendos (socios y accionistas)</option>
                                                    <option value="612 - Personas Físicas con Actividades Empresariales y Profesionales">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                                                    <option value="614 - Ingresos por intereses">614 - Ingresos por intereses</option>
                                                    <option value="615 - Régimen de los ingresos por obtención de premios">615 - Régimen de los ingresos por obtención de premios</option>
                                                    <option value="616 - Sin obligaciones fiscales">616 - Sin obligaciones fiscales</option>
                                                    <option value="620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</option>
                                                    <option value="621 - Incorporación Fiscal">621 - Incorporación Fiscal</option>
                                                    <option value="622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                                                    <option value="623 - Opcional para Grupos de Sociedades">623 - Opcional para Grupos de Sociedades</option>
                                                    <option value="624 - Coordinados">624 - Coordinados</option>
                                                    <option value="625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</option>
                                                    <option value="626 - Régimen Simplificado de Confianza">626 - Régimen Simplificado de Confianza</option>
                                                </select>
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-uso">Uso de CFDI</label>
                                                <select
                                                    id="prov-uso"
                                                    value={providerForm.usoCfdi}
                                                    onChange={(event) => handleProviderChange('usoCfdi', event.target.value)}
                                                    disabled={savingProvider}
                                                >
                                                    <option value="">Selecciona un uso de CFDI</option>
                                                    <option value="G01 - Adquisición de mercancías">G01 - Adquisición de mercancías</option>
                                                    <option value="G02 - Devoluciones, descuentos o bonificaciones">G02 - Devoluciones, descuentos o bonificaciones</option>
                                                    <option value="G03 - Gastos en general">G03 - Gastos en general</option>
                                                    <option value="I01 - Construcciones">I01 - Construcciones</option>
                                                    <option value="I02 - Mobiliario y equipo de oficina por inversiones">I02 - Mobiliario y equipo de oficina por inversiones</option>
                                                    <option value="I03 - Equipo de transporte">I03 - Equipo de transporte</option>
                                                    <option value="I04 - Equipo de computo y accesorios">I04 - Equipo de computo y accesorios</option>
                                                    <option value="I05 - Dados, troqueles, moldes, matrices y herramental">I05 - Dados, troqueles, moldes, matrices y herramental</option>
                                                    <option value="I06 - Comunicaciones telefónicas">I06 - Comunicaciones telefónicas</option>
                                                    <option value="I07 - Comunicaciones satelitales">I07 - Comunicaciones satelitales</option>
                                                    <option value="I08 - Otra maquinaria y equipo">I08 - Otra maquinaria y equipo</option>
                                                    <option value="D01 - Honorarios médicos, dentales y gastos hospitalarios">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                                                    <option value="D02 - Gastos médicos por incapacidad o discapacidad">D02 - Gastos médicos por incapacidad o discapacidad</option>
                                                    <option value="D03 - Gastos funerales">D03 - Gastos funerales</option>
                                                    <option value="D04 - Donativos">D04 - Donativos</option>
                                                    <option value="D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</option>
                                                    <option value="D06 - Aportaciones voluntarias al SAR">D06 - Aportaciones voluntarias al SAR</option>
                                                    <option value="D07 - Primas por seguros de gastos médicos">D07 - Primas por seguros de gastos médicos</option>
                                                    <option value="D08 - Gastos de transportación escolar obligatoria">D08 - Gastos de transportación escolar obligatoria</option>
                                                    <option value="D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                                                    <option value="D10 - Pagos por servicios educativos (colegiaturas)">D10 - Pagos por servicios educativos (colegiaturas)</option>
                                                    <option value="P01 - Por definir">P01 - Por definir</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cp-section">
                                        <div className="cp-section-header">
                                            <FaUniversity />
                                            <h3>Datos Bancarios</h3>
                                        </div>
                                        <div className="cp-fields-grid">
                                            <div className="cp-field">
                                                <label htmlFor="prov-banco">Banco o Institución</label>
                                                <select
                                                    id="prov-banco"
                                                    value={providerForm.banco}
                                                    onChange={(event) => handleProviderChange('banco', event.target.value)}
                                                    disabled={savingProvider}
                                                >
                                                    <option value="">Selecciona un banco o institución</option>
                                                    <optgroup label="Bancos">
                                                        <option value="BBVA México">BBVA México</option>
                                                        <option value="Santander">Santander</option>
                                                        <option value="Citibanamex">Citibanamex</option>
                                                        <option value="Banorte">Banorte</option>
                                                        <option value="HSBC">HSBC</option>
                                                        <option value="Scotiabank">Scotiabank</option>
                                                        <option value="Inbursa">Inbursa</option>
                                                        <option value="Azteca">Azteca</option>
                                                        <option value="Afirme">Afirme</option>
                                                        <option value="BanBajío">BanBajío</option>
                                                        <option value="Banregio">Banregio</option>
                                                        <option value="Invex">Invex</option>
                                                        <option value="Mifel">Mifel</option>
                                                        <option value="Banjercito">Banjercito</option>
                                                        <option value="Ve por Más">Ve por Más</option>
                                                        <option value="American Express">American Express</option>
                                                        <option value="Banco del Bienestar">Banco del Bienestar</option>
                                                        <option value="Intercam Banco">Intercam Banco</option>
                                                        <option value="Bancoppel">Bancoppel</option>
                                                        <option value="ABC Capital">ABC Capital</option>
                                                    </optgroup>
                                                    <optgroup label="Instituciones Financieras">
                                                        <option value="Actinver">Actinver</option>
                                                        <option value="Monex">Monex</option>
                                                        <option value="GBM">GBM</option>
                                                        <option value="Vector Casa de Bolsa">Vector Casa de Bolsa</option>
                                                        <option value="Kuspit Casa de Bolsa">Kuspit Casa de Bolsa</option>
                                                    </optgroup>
                                                    <optgroup label="Plataformas de Pago">
                                                        <option value="Mercado Pago">Mercado Pago</option>
                                                        <option value="PayPal">PayPal</option>
                                                        <option value="Stripe">Stripe</option>
                                                        <option value="Clip">Clip</option>
                                                        <option value="Conekta">Conekta</option>
                                                        <option value="OpenPay">OpenPay</option>
                                                        <option value="PayU">PayU</option>
                                                        <option value="2Checkout">2Checkout</option>
                                                        <option value="Square">Square</option>
                                                        <option value="Sr. Pago">Sr. Pago</option>
                                                    </optgroup>
                                                    <optgroup label="Monederos Digitales">
                                                        <option value="OXXO Pay">OXXO Pay</option>
                                                        <option value="CoDi">CoDi (Cobro Digital)</option>
                                                        <option value="Rappi Pay">Rappi Pay</option>
                                                        <option value="Ualá">Ualá</option>
                                                        <option value="Stori">Stori</option>
                                                        <option value="Klar">Klar</option>
                                                        <option value="Nu">Nu (Nubank)</option>
                                                    </optgroup>
                                                    <optgroup label="Otros">
                                                        <option value="Otro">Otro</option>
                                                    </optgroup>
                                                </select>
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-clabe">CLABE interbancaria</label>
                                                <input
                                                    id="prov-clabe"
                                                    type="text"
                                                    value={providerForm.clabe}
                                                    onChange={(event) => handleProviderChange('clabe', event.target.value)}
                                                    placeholder="18 dígitos"
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-metodo">Método de pago</label>
                                                <select
                                                    id="prov-metodo"
                                                    value={providerForm.metodoPago}
                                                    onChange={(event) => handleProviderChange('metodoPago', event.target.value)}
                                                    disabled={savingProvider}
                                                >
                                                    <option value="">Selecciona un método de pago</option>
                                                    <option value="PUE - Pago en una sola exhibición">PUE - Pago en una sola exhibición</option>
                                                    <option value="PPD - Pago en Parcialidades o Diferido">PPD - Pago en Parcialidades o Diferido</option>
                                                </select>
                                            </div>
                                            <div className="cp-field">
                                                <label htmlFor="prov-forma">Forma de pago</label>
                                                <select
                                                    id="prov-forma"
                                                    value={providerForm.formaPago}
                                                    onChange={(event) => handleProviderChange('formaPago', event.target.value)}
                                                    disabled={savingProvider}
                                                >
                                                    <option value="">Selecciona una forma de pago</option>
                                                    <option value="01 - Efectivo">01 - Efectivo</option>
                                                    <option value="02 - Cheque nominativo">02 - Cheque nominativo</option>
                                                    <option value="03 - Transferencia electrónica de fondos">03 - Transferencia electrónica de fondos</option>
                                                    <option value="04 - Tarjeta de crédito">04 - Tarjeta de crédito</option>
                                                    <option value="05 - Monedero electrónico">05 - Monedero electrónico</option>
                                                    <option value="06 - Dinero electrónico">06 - Dinero electrónico</option>
                                                    <option value="08 - Vales de despensa">08 - Vales de despensa</option>
                                                    <option value="12 - Dación en pago">12 - Dación en pago</option>
                                                    <option value="13 - Pago por subrogación">13 - Pago por subrogación</option>
                                                    <option value="14 - Pago por consignación">14 - Pago por consignación</option>
                                                    <option value="15 - Condonación">15 - Condonación</option>
                                                    <option value="17 - Compensación">17 - Compensación</option>
                                                    <option value="23 - Novación">23 - Novación</option>
                                                    <option value="24 - Confusión">24 - Confusión</option>
                                                    <option value="25 - Remisión de deuda">25 - Remisión de deuda</option>
                                                    <option value="26 - Prescripción o caducidad">26 - Prescripción o caducidad</option>
                                                    <option value="27 - A satisfacción del acreedor">27 - A satisfacción del acreedor</option>
                                                    <option value="28 - Tarjeta de débito">28 - Tarjeta de débito</option>
                                                    <option value="29 - Tarjeta de servicios">29 - Tarjeta de servicios</option>
                                                    <option value="30 - Aplicación de anticipos">30 - Aplicación de anticipos</option>
                                                    <option value="31 - Intermediario pagos">31 - Intermediario pagos</option>
                                                    <option value="99 - Por definir">99 - Por definir</option>
                                                </select>
                                            </div>
                                            <div className="cp-field cp-field--full">
                                                <label htmlFor="prov-documento">Enlace a documento (opcional)</label>
                                                <input
                                                    id="prov-documento"
                                                    type="url"
                                                    value={providerForm.documentoUrl}
                                                    onChange={(event) => handleProviderChange('documentoUrl', event.target.value)}
                                                    placeholder="https://..."
                                                    disabled={savingProvider}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cp-section">
                                        <div className="cp-section-header">
                                            <FaStickyNote />
                                            <h3>Notas internas</h3>
                                        </div>
                                        <div className="cp-field cp-field--full">
                                            <textarea
                                                id="prov-notas"
                                                value={providerForm.notas}
                                                onChange={(event) => handleProviderChange('notas', event.target.value)}
                                                placeholder="Observaciones adicionales, condiciones de pago, etc."
                                                rows={3}
                                                disabled={savingProvider}
                                            />
                                        </div>
                                    </div>

                                    <div className="cp-form-actions">
                                        <button type="button" className="cp-secondary" onClick={() => setProviderFormVisible(false)} disabled={savingProvider}>
                                            Cancelar
                                        </button>
                                        <button type="submit" className="cp-primary" disabled={savingProvider}>
                                            <FaCheck />
                                            <span>{selectedProviderId ? 'Guardar Cambios' : 'Registrar Proveedor'}</span>
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="cp-placeholder" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                                    <FaAddressBook size={64} style={{ color: '#e2e8f0', marginBottom: '1.5rem' }} />
                                    <h2 style={{ color: '#64748b', fontSize: '1.5rem', marginBottom: '1rem' }}>Agenda de proveedores</h2>
                                    <p style={{ color: '#94a3b8', maxWidth: '400px', margin: '0 auto' }}>Selecciona un proveedor de la lista para editarlo o presiona “Nuevo Proveedor” para registrar uno nuevo.</p>
                                </div>
                            )
                        ) : selectedProviderId ? (
                            <section className="cp-preview">
                                <div className="cp-form-header">
                                    <h2>Detalles del Proveedor</h2>
                                </div>
                                <div className="cp-preview-title" style={{ marginBottom: '2rem' }}>
                                    <h2 style={{ fontSize: '2rem', color: '#1e3a8a', margin: 0 }}>{providerForm.nombreEmpresa}</h2>
                                    <span style={{ color: '#64748b', fontSize: '0.9rem' }}>RFC: {providerForm.rfc || 'No registrado'}</span>
                                </div>
                                <div className="cp-preview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
                                    <article className="cp-preview-section">
                                        <div className="cp-section-header">
                                            <FaUser />
                                            <h3>Contacto</h3>
                                        </div>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                            {providerForm.nombreContacto && <li><strong style={{ color: '#475569' }}>Nombre:</strong> <br /> {providerForm.nombreContacto}</li>}
                                            {providerForm.telefono && <li><strong style={{ color: '#475569' }}>Teléfono:</strong> <br /> {providerForm.telefono}</li>}
                                            {providerForm.email && <li><strong style={{ color: '#475569' }}>Email:</strong> <br /> {providerForm.email}</li>}
                                            {providerForm.direccion && <li><strong style={{ color: '#475569' }}>Dirección:</strong> <br /> {providerForm.direccion}</li>}
                                        </ul>
                                    </article>
                                    <article className="cp-preview-section">
                                        <div className="cp-section-header">
                                            <FaIdCard />
                                            <h3>Fiscales</h3>
                                        </div>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                            {providerForm.razonSocial && <li><strong style={{ color: '#475569' }}>Razón social:</strong> <br /> {providerForm.razonSocial}</li>}
                                            {providerForm.regimenFiscal && <li><strong style={{ color: '#475569' }}>Régimen:</strong> <br /> {providerForm.regimenFiscal}</li>}
                                            {providerForm.usoCfdi && <li><strong style={{ color: '#475569' }}>Uso CFDI:</strong> <br /> {providerForm.usoCfdi}</li>}
                                        </ul>
                                    </article>
                                    <article className="cp-preview-section">
                                        <div className="cp-section-header">
                                            <FaUniversity />
                                            <h3>Pagos</h3>
                                        </div>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                            {providerForm.banco && <li><strong style={{ color: '#475569' }}>Banco:</strong> <br /> {providerForm.banco}</li>}
                                            {providerForm.clabe && <li><strong style={{ color: '#475569' }}>CLABE:</strong> <br /> {providerForm.clabe}</li>}
                                            {providerForm.metodoPago && <li><strong style={{ color: '#475569' }}>Método:</strong> <br /> {providerForm.metodoPago}</li>}
                                            {providerForm.formaPago && <li><strong style={{ color: '#475569' }}>Forma:</strong> <br /> {providerForm.formaPago}</li>}
                                        </ul>
                                    </article>
                                </div>
                                {providerForm.notas && (
                                    <div className="cp-preview-notes" style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '1rem', border: '1px solid #e2e8f0' }}>
                                        <div className="cp-section-header">
                                            <FaStickyNote />
                                            <h3>Notas</h3>
                                        </div>
                                        <p style={{ margin: 0, color: '#475569', lineHeight: '1.6' }}>{providerForm.notas}</p>
                                    </div>
                                )}
                            </section>
                        ) : (
                            <div className="cp-placeholder" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                                <FaAddressBook size={64} style={{ color: '#e2e8f0', marginBottom: '1.5rem' }} />
                                <p style={{ color: '#94a3b8' }}>Selecciona un proveedor de la lista para consultar sus detalles.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'facturacion' && (
                <div className="cp-facturacion" role="tabpanel" aria-label="Datos de facturación del fraccionamiento">
                    <div className="cp-form-panel">
                        {facturacionMessage && <div className="cp-feedback-message">{facturacionMessage}</div>}
                        {!facturacionLoaded ? (
                            <p className="cp-muted">Cargando información fiscal...</p>
                        ) : (
                            <form className="cp-form" onSubmit={handleSaveFacturacion}>
                                <div className="cp-form-header">
                                    <div className="cp-section-header">
                                        <FaIdCard />
                                        <h2>Datos de Facturación del Fraccionamiento</h2>
                                    </div>
                                    {canEdit && !isEditingFacturacion && (
                                        <button
                                            type="button"
                                            className="cp-primary"
                                            onClick={handleStartFacturacionEdit}
                                        >
                                            <FaEdit />
                                            <span>Editar Datos</span>
                                        </button>
                                    )}
                                </div>

                                {isEditingFacturacion && (
                                    <div className="cp-section">
                                        <div className="cp-section-header">
                                            <FaFileUpload />
                                            <h3>Actualizar vía PDF</h3>
                                        </div>
                                        <div className="cp-pdf-upload-section">
                                            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem', textAlign: 'center' }}>
                                                Carga la Constancia de Situación Fiscal (CSF) para auto-llenar los campos.
                                            </p>
                                            <label htmlFor="pdf-upload-facturacion" className="cp-pdf-upload-label" style={{ display: 'block' }}>
                                                <input
                                                    id="pdf-upload-facturacion"
                                                    type="file"
                                                    accept="application/pdf"
                                                    onChange={handlePDFUploadFacturacion}
                                                    style={{ display: 'none' }}
                                                />
                                                <div className="cp-pdf-upload-button">
                                                    <FaFileUpload size={32} />
                                                    <span>Seleccionar PDF</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="cp-section">
                                    <div className="cp-fields-grid">
                                        <div className="cp-field">
                                            <label htmlFor="fact-nombre">Nombre del fraccionamiento</label>
                                            <input
                                                id="fact-nombre"
                                                type="text"
                                                value={facturacion.nombreFraccionamiento}
                                                onChange={(event) => handleFacturacionChange('nombreFraccionamiento', event.target.value)}
                                                placeholder="Residencial Los Olivos"
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            />
                                        </div>
                                        <div className="cp-field">
                                            <label htmlFor="fact-razon">Razón social</label>
                                            <input
                                                id="fact-razon"
                                                type="text"
                                                value={facturacion.razonSocial}
                                                onChange={(event) => handleFacturacionChange('razonSocial', event.target.value)}
                                                placeholder="Administradora Los Olivos S.A. de C.V."
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            />
                                        </div>
                                        <div className="cp-field">
                                            <label htmlFor="fact-rfc">RFC</label>
                                            <input
                                                id="fact-rfc"
                                                type="text"
                                                value={facturacion.rfc}
                                                onChange={(event) => handleFacturacionChange('rfc', event.target.value.toUpperCase())}
                                                placeholder="AL0150720XX9"
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            />
                                        </div>
                                        <div className="cp-field cp-field--full">
                                            <label htmlFor="fact-domicilio">Domicilio fiscal</label>
                                            <input
                                                id="fact-domicilio"
                                                type="text"
                                                value={facturacion.domicilioFiscal}
                                                onChange={(event) => handleFacturacionChange('domicilioFiscal', event.target.value)}
                                                placeholder="Av. Principal 123, San Juan del Río, Querétaro"
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            />
                                        </div>
                                        <div className="cp-field">
                                            <label htmlFor="fact-regimen">Régimen fiscal</label>
                                            <select
                                                id="fact-regimen"
                                                value={facturacion.regimenFiscal}
                                                onChange={(event) => handleFacturacionChange('regimenFiscal', event.target.value)}
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            >
                                                <option value="">Selecciona un régimen fiscal</option>
                                                <option value="601 - General de Ley Personas Morales">601 - General de Ley Personas Morales</option>
                                                <option value="603 - Personas Morales con Fines no Lucrativos">603 - Personas Morales con Fines no Lucrativos</option>
                                                <option value="605 - Sueldos y Salarios e Ingresos Asimilados a Salarios">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                                                <option value="606 - Arrendamiento">606 - Arrendamiento</option>
                                                <option value="607 - Régimen de Enajenación o Adquisición de Bienes">607 - Régimen de Enajenación o Adquisición de Bienes</option>
                                                <option value="608 - Demás ingresos">608 - Demás ingresos</option>
                                                <option value="610 - Residentes en el Extranjero sin Establecimiento Permanente en México">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</option>
                                                <option value="611 - Ingresos por Dividendos (socios y accionistas)">611 - Ingresos por Dividendos (socios y accionistas)</option>
                                                <option value="612 - Personas Físicas con Actividades Empresariales y Profesionales">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                                                <option value="614 - Ingresos por intereses">614 - Ingresos por intereses</option>
                                                <option value="615 - Régimen de los ingresos por obtención de premios">615 - Régimen de los ingresos por obtención de premios</option>
                                                <option value="616 - Sin obligaciones fiscales">616 - Sin obligaciones fiscales</option>
                                                <option value="620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</option>
                                                <option value="621 - Incorporación Fiscal">621 - Incorporación Fiscal</option>
                                                <option value="622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                                                <option value="623 - Opcional para Grupos de Sociedades">623 - Opcional para Grupos de Sociedades</option>
                                                <option value="624 - Coordinados">624 - Coordinados</option>
                                                <option value="625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</option>
                                                <option value="626 - Régimen Simplificado de Confianza">626 - Régimen Simplificado de Confianza</option>
                                            </select>
                                        </div>
                                        <div className="cp-field">
                                            <label htmlFor="fact-uso">Uso de CFDI habitual</label>
                                            <select
                                                id="fact-uso"
                                                value={facturacion.usoCfdi}
                                                onChange={(event) => handleFacturacionChange('usoCfdi', event.target.value)}
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            >
                                                <option value="">Selecciona un uso de CFDI</option>
                                                <option value="G01 - Adquisición de mercancías">G01 - Adquisición de mercancías</option>
                                                <option value="G02 - Devoluciones, descuentos o bonificaciones">G02 - Devoluciones, descuentos o bonificaciones</option>
                                                <option value="G03 - Gastos en general">G03 - Gastos en general</option>
                                                <option value="I01 - Construcciones">I01 - Construcciones</option>
                                                <option value="I02 - Mobiliario y equipo de oficina por inversiones">I02 - Mobiliario y equipo de oficina por inversiones</option>
                                                <option value="I03 - Equipo de transporte">I03 - Equipo de transporte</option>
                                                <option value="I04 - Equipo de computo y accesorios">I04 - Equipo de computo y accesorios</option>
                                                <option value="I05 - Dados, troqueles, moldes, matrices y herramental">I05 - Dados, troqueles, moldes, matrices y herramental</option>
                                                <option value="I06 - Comunicaciones telefónicas">I06 - Comunicaciones telefónicas</option>
                                                <option value="I07 - Comunicaciones satelitales">I07 - Comunicaciones satelitales</option>
                                                <option value="I08 - Otra maquinaria y equipo">I08 - Otra maquinaria y equipo</option>
                                                <option value="D01 - Honorarios médicos, dentales y gastos hospitalarios">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                                                <option value="D02 - Gastos médicos por incapacidad o discapacidad">D02 - Gastos médicos por incapacidad o discapacidad</option>
                                                <option value="D03 - Gastos funerales">D03 - Gastos funerales</option>
                                                <option value="D04 - Donativos">D04 - Donativos</option>
                                                <option value="D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</option>
                                                <option value="D06 - Aportaciones voluntarias al SAR">D06 - Aportaciones voluntarias al SAR</option>
                                                <option value="D07 - Primas por seguros de gastos médicos">D07 - Primas por seguros de gastos médicos</option>
                                                <option value="D08 - Gastos de transportación escolar obligatoria">D08 - Gastos de transportación escolar obligatoria</option>
                                                <option value="D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                                                <option value="D10 - Pagos por servicios educativos (colegiaturas)">D10 - Pagos por servicios educativos (colegiaturas)</option>
                                                <option value="P01 - Por definir">P01 - Por definir</option>
                                            </select>
                                        </div>
                                        <div className="cp-field">
                                            <label htmlFor="fact-telefono">Teléfono</label>
                                            <input
                                                id="fact-telefono"
                                                type="tel"
                                                value={facturacion.telefono}
                                                onChange={(event) => handleFacturacionChange('telefono', event.target.value)}
                                                placeholder="427-272-0000"
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            />
                                        </div>
                                        <div className="cp-field">
                                            <label htmlFor="fact-email">Email de facturación</label>
                                            <input
                                                id="fact-email"
                                                type="email"
                                                value={facturacion.emailFacturacion}
                                                onChange={(event) => handleFacturacionChange('emailFacturacion', event.target.value)}
                                                placeholder="facturacion@losolivos.com"
                                                disabled={!isEditingFacturacion || savingFacturacion}
                                            />
                                        </div>
                                    </div>
                                    <div className="cp-field cp-field--full" style={{ marginTop: '1.25rem' }}>
                                        <label htmlFor="fact-notas">Notas para proveedores</label>
                                        <textarea
                                            id="fact-notas"
                                            rows={3}
                                            value={facturacion.notas}
                                            onChange={(event) => handleFacturacionChange('notas', event.target.value)}
                                            placeholder="Ej. Se reciben facturas hasta el día 25 de cada mes."
                                            disabled={!isEditingFacturacion || savingFacturacion}
                                        />
                                    </div>
                                </div>

                                {isEditingFacturacion ? (
                                    <div className="cp-form-actions">
                                        <button
                                            type="button"
                                            className="cp-secondary"
                                            onClick={handleCancelFacturacionEdit}
                                            disabled={savingFacturacion}
                                        >
                                            Cancelar
                                        </button>
                                        <button type="submit" className="cp-primary" disabled={savingFacturacion}>
                                            <FaCheck />
                                            <span>Guardar Datos de Facturación</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ marginTop: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.75rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                        <p className="cp-muted" style={{ margin: 0, fontSize: '0.9rem' }}>Solo lectura. Contacta a la administración si necesitas actualizar esta información.</p>
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={Boolean(providerToDelete)}
                title="Eliminar proveedor"
                message={providerToDelete ? `¿Eliminar "${providerToDelete.nombreEmpresa || 'este proveedor'}" de la agenda de proveedores?` : ''}
                cancelLabel="Conservar"
                confirmLabel="Eliminar"
                tone="danger"
                confirmDisabled={deletingProvider}
                onCancel={() => {
                    if (!deletingProvider) {
                        setProviderToDelete(null);
                    }
                }}
                onConfirm={handleConfirmDeleteProvider}
            />
        </div>
    );
};

export default DatosContactoProveedores;
